import type { jsPDF } from 'jspdf';

import { formatNumber } from '~/utils/common';
import { drawTableCell } from '~/utils/pdf/pdfDrawHelpers';

import { drawLabelValueRow, drawQRCodeCell, getFontSizeByLength, getResponsiveFontSize } from './labelHelpers';
import { LABEL_LAYOUT } from './labelLayoutConfig';

import type { TypeReceivingLabelLabel } from '~/types/receive.types';

/**
 * 라벨 셀 옵션 (헤더 셀)
 */
interface TypeLabelCellOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  fontSize?: number;
  textOffsetX?: number;
  textOffsetY?: number;
}

/**
 * 값 셀 옵션
 */
interface TypeValueCellOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  fontSize?: number;
  fontStyle?: 'normal' | 'bold';
  align?: 'left' | 'center';
  textOffsetY?: number;
}

/**
 * 공통 라벨 셀 그리기 (헤더 배경, 일반체, 중앙정렬)
 */
const drawLabelCell = (pdf: jsPDF, options: TypeLabelCellOptions) => {
  drawTableCell(pdf, {
    x: options.x,
    y: options.y,
    width: options.width,
    height: options.height,
    fillColor: LABEL_LAYOUT.colors.headerBg,
    borderColor: LABEL_LAYOUT.border.color,
    borderWidth: LABEL_LAYOUT.border.width,
    text: options.text,
    fontSize: options.fontSize ?? LABEL_LAYOUT.fonts.medium,
    fontStyle: 'normal',
    align: 'center',
    textOffsetX: options.textOffsetX ?? 0,
    textOffsetY: options.textOffsetY,
  });
};

/**
 * 공통 값 셀 그리기
 */
const drawValueCell = (pdf: jsPDF, options: TypeValueCellOptions) => {
  drawTableCell(pdf, {
    x: options.x,
    y: options.y,
    width: options.width,
    height: options.height,
    borderColor: LABEL_LAYOUT.border.color,
    borderWidth: LABEL_LAYOUT.border.width,
    text: options.text,
    fontSize: options.fontSize ?? LABEL_LAYOUT.fonts.medium,
    fontStyle: options.fontStyle,
    align: options.align,
    textOffsetY: options.textOffsetY,
  });
};

/**
 * 입하 라벨 전체를 그리는 함수
 * 12행 x 6그리드 구조
 */
export const drawFullLabel = async (pdf: jsPDF, label: TypeReceivingLabelLabel, startY: number): Promise<number> => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const { page, rows, border } = LABEL_LAYOUT;

  const tableWidth = pageWidth - page.margin * 2;
  const x = page.margin;
  let currentY = startY;

  const rowHeight = rows.standard;
  const titleRowHeight = rows.title;

  // 6 그리드 시스템
  const gridWidth = tableWidth / 6; // 1그리드 너비
  const grid1 = gridWidth; // 1칸
  const grid2 = gridWidth * 2; // 2칸
  const grid3 = gridWidth * 3; // 3칸
  const grid6 = gridWidth * 6; // 6칸 (전체)

  // ========== Row 1: 입고요청번호 (4.5그리드) + QR1 (1.5그리드, row 1~3) ==========
  const row1Y = currentY;
  const qr1CellWidth = gridWidth * LABEL_LAYOUT.grid.qr1Width; // QR1 너비 (1.5그리드)
  const row1ContentWidth = gridWidth * (LABEL_LAYOUT.grid.totalColumns - LABEL_LAYOUT.grid.qr1Width); // 입고요청번호 영역 (4.5그리드)

  // 입고요청번호 라벨 (1그리드)
  drawLabelCell(pdf, {
    x,
    y: row1Y,
    width: grid1,
    height: rowHeight,
    text: '입고요청번호',
    textOffsetX: -1,
  });

  // 입고요청번호 값 (3.5그리드)
  drawValueCell(pdf, {
    x: x + grid1,
    y: row1Y,
    width: gridWidth * 3.5,
    height: rowHeight,
    text: label.receivingRequestCode,
    fontStyle: 'bold',
  });

  // QR1 셀 (1.5그리드, row 1~3 병합)
  const qr1CellHeight = rowHeight * LABEL_LAYOUT.grid.qr1Rows;
  await drawQRCodeCell(pdf, x + row1ContentWidth, row1Y, qr1CellWidth, qr1CellHeight, label.receivingRequestCode);

  currentY += rowHeight;

  // ========== Row 2: 입고센터 (4.5그리드) ==========
  const row2Y = currentY;

  drawLabelValueRow(pdf, x, row2Y, gridWidth, rowHeight, {
    label: '입고센터',
    value: label.inboundAreaName || '',
    labelWidth: 1,
    valueWidth: 3.5,
  });

  currentY += rowHeight;

  // ========== Row 3: 입고예정일 (4.5그리드) ==========
  const row3Y = currentY;

  drawLabelValueRow(pdf, x, row3Y, gridWidth, rowHeight, {
    label: '입고예정일',
    value: label.receivingExpectedDate || '',
    labelWidth: 1,
    valueWidth: 3.5,
    valueOptions: { fontSize: 12, fontStyle: 'bold' },
  });

  currentY += rowHeight;

  // ========== Row 4: 화주사 (6그리드 전체) ==========
  const row4Y = currentY;

  // 화주사 라벨 (1그리드)
  drawLabelCell(pdf, {
    x,
    y: row4Y,
    width: grid1,
    height: rowHeight,
    text: '화주사',
  });

  // 화주사 값 (5그리드)
  const ownerText = `(${label.ownerCode || ''}) ${label.ownerName || ''}`;
  const ownerFontSize = getFontSizeByLength(
    ownerText,
    LABEL_LAYOUT.responsiveFontSizes.owner.threshold,
    LABEL_LAYOUT.responsiveFontSizes.owner.defaultFontSize,
    LABEL_LAYOUT.responsiveFontSizes.owner.smallFontSize
  );
  drawValueCell(pdf, {
    x: x + grid1,
    y: row4Y,
    width: gridWidth * 5,
    height: rowHeight,
    text: ownerText,
    fontSize: ownerFontSize,
    fontStyle: 'bold',
  });

  currentY += rowHeight;

  // ========== Row 5: 공급사 (6그리드 전체) ==========
  const row5Y = currentY;

  // 공급사 라벨 (1그리드)
  drawLabelCell(pdf, {
    x,
    y: row5Y,
    width: grid1,
    height: rowHeight,
    text: '공급사',
  });

  // 공급사 값 (5그리드)
  const supplierText = `(${label.supplierCode || ''}) ${label.supplierName || ''}`;
  const { fontSize: supplierFontSize, textOffsetY: supplierTextOffsetY } = getResponsiveFontSize(
    supplierText,
    LABEL_LAYOUT.responsiveFontSizes.supplier
  );
  drawValueCell(pdf, {
    x: x + grid1,
    y: row5Y,
    width: gridWidth * 5,
    height: rowHeight,
    text: supplierText,
    fontSize: supplierFontSize,
    fontStyle: 'bold',
    textOffsetY: supplierTextOffsetY,
  });

  currentY += rowHeight;

  // ========== Row 6: 소비기한 (3그리드) + 제조일자 (3그리드) ==========
  const row6Y = currentY;

  // 소비기한
  drawLabelValueRow(pdf, x, row6Y, gridWidth, rowHeight, {
    label: '소비기한',
    value: label.expirationDate || '',
    labelWidth: 1,
    valueWidth: 2,
    valueOptions: { align: 'center' },
  });

  // 제조일자
  drawLabelValueRow(pdf, x + grid3, row6Y, gridWidth, rowHeight, {
    label: '제조일자',
    value: label.manufactureDate || '',
    labelWidth: 1,
    valueWidth: 2,
    valueOptions: { align: 'center' },
  });

  currentY += rowHeight;

  // ========== Row 7: 전체 박스 수 (2그리드) + 박스 내 수량(EA) (2그리드) + 총 발주수량(EA) (2그리드) ==========
  const row7Y = currentY;

  // 전체 박스 수 라벨 (1그리드)
  drawLabelCell(pdf, {
    x,
    y: row7Y,
    width: grid1,
    height: rowHeight,
    text: '전체 박스 수',
  });

  // 전체 박스 수 값 (1그리드)
  drawValueCell(pdf, {
    x: x + grid1,
    y: row7Y,
    width: grid1,
    height: rowHeight,
    text: formatNumber(label.totalBoxCnt || 0),
    align: 'center',
  });

  // 박스 내 수량(EA) 라벨 (1그리드)
  drawLabelCell(pdf, {
    x: x + grid2,
    y: row7Y,
    width: grid1,
    height: rowHeight,
    text: '박스 내 수량\n(EA)',
    textOffsetY: -0.5,
  });

  // 박스 내 수량(EA) 값 (1그리드)
  drawValueCell(pdf, {
    x: x + grid2 + grid1,
    y: row7Y,
    width: grid1,
    height: rowHeight,
    text: formatNumber(label.unitPerBox || 0),
    align: 'center',
  });

  // 총 발주수량(EA) 라벨 (1그리드)
  drawLabelCell(pdf, {
    x: x + grid2 * 2,
    y: row7Y,
    width: grid1,
    height: rowHeight,
    text: '총 발주 수량\n(EA)',
    textOffsetY: -0.5,
  });

  // 총 발주수량(EA) 값 (1그리드)
  drawValueCell(pdf, {
    x: x + grid2 * 2 + grid1,
    y: row7Y,
    width: grid1,
    height: rowHeight,
    text: formatNumber(label.totalUnit || 0),
    fontStyle: 'bold',
    align: 'center',
  });

  currentY += rowHeight;

  // ========== Row 8: 상품명 (6그리드 전체, 큰 글씨) ==========
  const row8Y = currentY;

  // 상품명 길이에 따라 폰트 크기와 textOffsetY 결정
  const { fontSize: titleFontSize, textOffsetY: titleTextOffsetY } = getResponsiveFontSize(
    label.goodsName,
    LABEL_LAYOUT.responsiveFontSizes.goodsName
  );

  drawTableCell(pdf, {
    x,
    y: row8Y,
    width: grid6,
    height: titleRowHeight,
    borderColor: border.color,
    borderWidth: border.width,
    text: label.goodsName,
    fontSize: titleFontSize,
    fontStyle: 'bold',
    align: 'center',
    textOffsetY: titleTextOffsetY,
  });

  currentY += titleRowHeight;

  // ========== Row 9: 샛별오더 (3그리드) + 적치존 (3그리드) ==========
  const row9Y = currentY;

  // 샛별오더 라벨 (1그리드)
  drawLabelCell(pdf, {
    x,
    y: row9Y,
    width: grid1,
    height: rowHeight,
    text: '샛별오더',
  });

  // 샛별오더 값 (2그리드)
  drawValueCell(pdf, {
    x: x + grid1,
    y: row9Y,
    width: grid2,
    height: rowHeight,
    text: label.deliveryType || '',
    fontSize: 12,
    align: 'center',
    fontStyle: 'bold',
  });

  // 적치존 라벨 (1그리드)
  drawLabelCell(pdf, {
    x: x + grid3,
    y: row9Y,
    width: grid1,
    height: rowHeight,
    text: '적치존',
  });

  // 적치존 값 (2그리드)
  drawValueCell(pdf, {
    x: x + grid3 + grid1,
    y: row9Y,
    width: grid2,
    height: rowHeight,
    text: label.putawayZone || '',
    fontSize: 12,
    align: 'center',
    fontStyle: 'bold',
  });

  currentY += rowHeight;

  // ========== Row 10~12: QR2 (1.5그리드) + 상품코드/대체코드/검품여부 (4.5그리드) ==========
  const row10Y = currentY;

  // QR2 셀 (1.5그리드, row 10~12 병합)
  const qr2CellWidth = gridWidth * LABEL_LAYOUT.grid.qr2Width;
  const qr2CellHeight = rowHeight * LABEL_LAYOUT.grid.qr2Rows;
  await drawQRCodeCell(pdf, x, row10Y, qr2CellWidth, qr2CellHeight, label.goodsCode);

  // 나머지 너비 (4.5그리드)
  const remainingLabelWidth = gridWidth * 1; // 라벨 1그리드
  const remainingValueWidth = gridWidth * 3.5; // 값 3.5그리드

  // Row 10: 상품코드
  // 상품코드 라벨 (1그리드)
  drawLabelCell(pdf, {
    x: x + qr2CellWidth,
    y: row10Y,
    width: remainingLabelWidth,
    height: rowHeight,
    text: '상품코드',
  });

  // 상품코드 값 (3.5그리드)
  drawValueCell(pdf, {
    x: x + qr2CellWidth + remainingLabelWidth,
    y: row10Y,
    width: remainingValueWidth,
    height: rowHeight,
    text: label.goodsCode,
    fontSize: 12,
    fontStyle: 'bold',
    align: 'center',
  });

  currentY += rowHeight;

  // Row 11: 대체코드
  const row11Y = currentY;

  // 대체코드 라벨 (1그리드)
  drawLabelCell(pdf, {
    x: x + qr2CellWidth,
    y: row11Y,
    width: remainingLabelWidth,
    height: rowHeight,
    text: '대체코드',
  });

  // 대체코드 값 (3.5그리드)
  drawValueCell(pdf, {
    x: x + qr2CellWidth + remainingLabelWidth,
    y: row11Y,
    width: remainingValueWidth,
    height: rowHeight,
    text: label.alternativeCode || '',
    fontSize: 12,
    align: 'center',
    fontStyle: 'bold',
  });

  currentY += rowHeight;

  // Row 12: 검품여부
  const row12Y = currentY;

  // 검품여부 라벨 (1그리드)
  drawLabelCell(pdf, {
    x: x + qr2CellWidth,
    y: row12Y,
    width: remainingLabelWidth,
    height: rowHeight,
    text: '검품여부',
  });

  // 검품여부 값 (3.5그리드)
  drawValueCell(pdf, {
    x: x + qr2CellWidth + remainingLabelWidth,
    y: row12Y,
    width: remainingValueWidth,
    height: rowHeight,
    text: label.isQi ? 'Y' : 'N',
    fontSize: 12,
    align: 'center',
    fontStyle: 'bold',
  });

  currentY += rowHeight;

  return currentY;
};
